import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  UserInfoInterface,
  DEFAULT_USER,
  UserPermissionsInterface,
  LoginInterface,
  LoginResponseInterface,
  OtpVerifyInterface,
  ForgotPasswordInterface,
  ResetPasswordInterface,
  UserPermissionDetailsInterface,
  UserDetailsInterface,
} from '@core/models/authentication.model';
import { API_URL_CONSTANT } from '@sharedModule/constants/api-url.constant';
import { ROUTES } from '@sharedModule/constants/route.constants';
import { ResponseIdentity } from '@sharedModule/models/server-response-identity.model';
import { isNonArrayObject } from '@sharedModule/utils/type-guard.util';
import { BehaviorSubject, Observable } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { HttpClientService } from '../http-client/http-client.service';
import { SessionStorageService } from '../session-storage/session-storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly TOKEN_KEY = 'xbpPartnerToken';
  private readonly USER_DATA_KEY = 'userData';
  private readonly SUPER_ADMIN_KEY = 'isSuperAdmin';

  // State management
  public readonly userInfoSubject = new BehaviorSubject<UserInfoInterface>(
    DEFAULT_USER
  );
  public readonly permissionsSubject = new BehaviorSubject<
    UserPermissionsInterface[]
  >([]);

  // Public observables
  readonly userInfo$ = this.userInfoSubject.asObservable();
  readonly permissions$ = this.permissionsSubject.asObservable();

  constructor(
    private readonly router: Router,
    private readonly httpClient: HttpClientService,
    private readonly sessionStorageService: SessionStorageService
  ) {}

  // Token Management
  checkLogin(): boolean {
    const access_token = this.getToken();
    return !!access_token && !this.isTokenExpired(access_token);
  }

  getToken(): string | null {
    return this.sessionStorageService.get(this.TOKEN_KEY);
  }

  isTokenExpired(token: string): boolean {
    const expiryDate = this.getTokenExpiryDate(token);
    const currentDate = new Date();
    return !expiryDate || currentDate >= expiryDate;
  }

  getTokenExpiryDate(token: string): Date | null {
    const decodedToken = this.decodeToken(token);

    if (!this.hasExpProperty(decodedToken)) {
      return null;
    }

    const expirationDate = new Date(0);
    expirationDate.setUTCSeconds(decodedToken.exp);
    return expirationDate;
  }

  hasExpProperty(token: unknown): token is { exp: number } {
    return (
      isNonArrayObject(token) && 'exp' in token && typeof token.exp === 'number'
    );
  }

  decodeToken(token: string): { exp: number } | null {
    const decodedToken = jwtDecode<{ exp: number }>(token);

    if (!decodedToken || !decodedToken.exp) {
      this.logout();
      return null;
    }

    return decodedToken;
  }

  // Authentication Methods
  login(
    payload: LoginInterface
  ): Observable<ResponseIdentity<LoginResponseInterface>> {
    return this.httpClient.post(API_URL_CONSTANT.authentication.logIn, payload);
  }

  verifyLoginOTP(
    payload: OtpVerifyInterface
  ): Observable<ResponseIdentity<LoginResponseInterface>> {
    return this.httpClient.post(
      API_URL_CONSTANT.authentication.verifyLoginOTP,
      payload
    );
  }

  forgotPassword(
    payload: ForgotPasswordInterface
  ): Observable<ResponseIdentity<null>> {
    return this.httpClient.post(
      API_URL_CONSTANT.authentication.forgotPassword,
      payload
    );
  }

  verifyForgotPasswordOTP(
    payload: OtpVerifyInterface
  ): Observable<ResponseIdentity<{ token: string }>> {
    return this.httpClient.post(
      API_URL_CONSTANT.authentication.verifyForgotPasswordOTP,
      payload
    );
  }

  resetPassword(
    payload: ResetPasswordInterface
  ): Observable<ResponseIdentity<null>> {
    return this.httpClient.post(
      API_URL_CONSTANT.authentication.resetPassword,
      payload
    );
  }

  getUserPermissions(): Observable<
    ResponseIdentity<UserPermissionDetailsInterface>
  > {
    return this.httpClient.post(
      API_URL_CONSTANT.authentication.getUserPermissions
    );
  }

  // User Session Management
  onLoggedIn(response: LoginResponseInterface): void {
    if (!response?.token) {
      throw new Error('Invalid login response');
    }

    this.sessionStorageService.set(this.TOKEN_KEY, response.token);
    this.router.navigate([ROUTES.TRANSACTION_LIST]);
  }

  public createAuthUser(
    userDetails: UserDetailsInterface,
    partnerLogo?: string
  ): UserInfoInterface {
    const middleName = this.formatMiddleName(
      userDetails.middle_name,
      userDetails.last_name
    );

    return {
      userName: `${userDetails.first_name}${middleName}${userDetails.last_name}`,
      userEmail: userDetails.email,
      userId: userDetails.user_id,
      // Sidenav branding comes from the partner logo (falls back to the user's).
      logo: partnerLogo || userDetails.logo,
      mode: userDetails.mode,
      partnerId: userDetails.partner_id,
    };
  }

  private formatMiddleName(middleName: string, lastName: string): string {
    if (middleName && middleName.trim()) {
      return ` ${middleName} `;
    }
    return lastName && lastName.trim() ? ' ' : '';
  }

  // Admin Check
  isSuperAdmin(): boolean {
    const superAdminStatus = this.sessionStorageService.get(
      this.SUPER_ADMIN_KEY
    );
    return superAdminStatus === 'true';
  }

  // Logout
  logout(): void {
    this.permissionsSubject.next([]);
    this.sessionStorageService.clear();
    this.router.navigate([ROUTES.LOGIN]);
  }

  // Utility Methods
  getUserPermission(): UserPermissionsInterface[] {
    return this.permissionsSubject.value;
  }

  getUserInfo(): UserInfoInterface {
    return this.userInfoSubject.value;
  }

  isLoggedIn(): boolean {
    return this.checkLogin();
  }
}
